
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Card, Typography, TextField, Button, Switch,
    FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, CircularProgress, IconButton, Tooltip, Paper, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Divider
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, Edit as EditIcon, Cancel as CancelIcon, Settings as SettingsIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import { API_BASE, CRON_SERVICE_URL, CRON_API_KEY } from '../config';

interface CronSetting {
    jobName: string;
    cronExpression: string;
    description: string;
    isActive: boolean;
    lastUpdated: string;
}

interface BusinessRule {
    key: string;
    value: any;
    description: string;
}

// Helper function to convert cron to human-readable format
const cronToReadable = (cron: string): string => {
    const parts = cron.trim().split(' ');
    if (parts.length !== 5) return cron;

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Check for daily pattern
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
        const h = parseInt(hour);
        const m = parseInt(minute);
        const period = h >= 12 ? 'PM' : 'AM';
        const displayHour = h > 12 ? h - 12 : (h === 0 ? 12 : h);
        return `Daily at ${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
    }

    return cron; // Fallback to original
};

// Helper function to convert human-readable back to cron
const readableToCron = (readable: string): string => {
    // Extract time from "Daily at HH:MM AM/PM" format
    const match = readable.match(/Daily at (\d{1,2}):(\d{2}) (AM|PM)/);
    if (match) {
        let hour = parseInt(match[1]);
        const minute = match[2];
        const period = match[3];

        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;

        return `${minute} ${hour} * * *`;
    }
    return readable; // Fallback
};

// Parse cron to get hour and minute for editing
const parseCronTime = (cron: string): { hour: number; minute: number } => {
    const parts = cron.trim().split(' ');
    if (parts.length >= 2) {
        return {
            minute: parseInt(parts[0]) || 0,
            hour: parseInt(parts[1]) || 0
        };
    }
    return { hour: 0, minute: 0 };
};

const CronManager: React.FC = () => {
    const [tab, setTab] = useState(0);
    const [settings, setSettings] = useState<CronSetting[]>([]);
    const [rules, setRules] = useState<BusinessRule[]>([]);
    const [cronSchedules, setCronSchedules] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [editing, setEditing] = useState<string | null>(null);
    const [tempCron, setTempCron] = useState('');
    const [tempHour, setTempHour] = useState(12);
    const [tempMinute, setTempMinute] = useState(0);
    const [tempPeriod, setTempPeriod] = useState<'AM' | 'PM'>('AM');
    const [tempRuleValue, setTempRuleValue] = useState<string>('');
    const [complexEditRule, setComplexEditRule] = useState<BusinessRule | null>(null);
    const [tempObjectValue, setTempObjectValue] = useState<any>({});
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [serviceHealth, setServiceHealth] = useState<'UP' | 'DOWN' | 'DEGRADED' | 'CHECKING' | 'IDLE' | 'RESTARTING'>('CHECKING');
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [manualDate, setManualDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [runLoading, setRunLoading] = useState<string | null>(null);
    const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);

    const fetchSettings = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            // Fetch both business rules (for cron expressions) and cron-settings (for descriptions)
            const [rulesRes, cronRes] = await Promise.all([
                axios.get(`${API_BASE}/api/business-rules`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE}/api/cron-settings`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            if (rulesRes.data.status === 'success') {
                const cronRule = rulesRes.data.data.find((r: any) => r.key === 'CRON_SCHEDULES');
                if (cronRule) {
                    setCronSchedules(cronRule.value || {});
                }
            }

            // Store individual cron settings for descriptions
            if (cronRes.data.status === 'success') {
                setSettings(cronRes.data.data);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchRules = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE}/api/business-rules`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.status === 'success') {
                // Filter out CRON_SCHEDULES as it's managed in the Cron Schedules tab
                setRules(res.data.data.filter((r: BusinessRule) => r.key !== 'CRON_SCHEDULES'));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch business rules');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 0) fetchSettings();
        else fetchRules();
        checkServiceHealth(); // Check health on tab change
    }, [tab]);

    // Health check polling
    useEffect(() => {
        checkServiceHealth();
        const healthInterval = setInterval(checkServiceHealth, 10000); // Check every 10 seconds
        return () => clearInterval(healthInterval);
    }, []);

    // Cooldown timer
    useEffect(() => {
        if (cooldownSeconds > 0) {
            const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setServiceHealth(prev => prev === 'RESTARTING' ? 'DOWN' : prev);
        }
    }, [cooldownSeconds]);

    const checkServiceHealth = async () => {
        try {
            // /health is public — no API key needed
            const res = await axios.get(`${CRON_SERVICE_URL}/health`, {
                timeout: 5000
            });

            // Log for debugging
            console.log('Cron Health Check Response:', res.data);

            if (res.data.status === 'UP') {
                setServiceHealth('UP');
            } else if (res.data.status === 'DEGRADED') {
                setServiceHealth('DEGRADED');
            } else if (res.data.status === 'IDLE') {
                setServiceHealth('IDLE');
            } else {
                setServiceHealth('DOWN');
            }
        } catch (err: any) {
            console.error('Cron Health Check Error:', err);
            setServiceHealth(prev => prev === 'RESTARTING' ? 'RESTARTING' : 'DOWN');
        }
    };

    const restartCronService = async (silent = false) => {
        if (!silent) setActionLoading('restart');
        try {
            const res = await axios.post(`${CRON_SERVICE_URL}/admin/system/restart`, {}, {
                headers: { 'X-Api-Key': CRON_API_KEY }
            });
            if (res.data.status === 'success') {
                if (!silent) {
                    setSuccess(`System restart initiated: Restarting server... Please wait 15-20 seconds.`);
                }
                setCooldownSeconds(20);
                setServiceHealth('RESTARTING');
                setHasPendingChanges(false);
                setTimeout(() => checkServiceHealth(), 10000);
            }
        } catch (err: any) {
            if (!silent) {
                if ((err as any).response?.status === 401) {
                    setError(`Authentication failed: Check REACT_APP_CRON_API_KEY in your .env file.`);
                } else {
                    setError(`Failed to restart: ` + (err.response?.data?.message || err.message));
                }
            } else {
                console.error("Silent restart failed", err);
            }
        } finally {
            if (!silent) setActionLoading(null);
        }
    };

    const handleUpdateCronSchedule = async (jobKey: string) => {
        try {
            // Convert 12-hour format to 24-hour for cron
            let hour24 = tempHour;
            if (tempPeriod === 'PM' && tempHour !== 12) {
                hour24 = tempHour + 12;
            } else if (tempPeriod === 'AM' && tempHour === 12) {
                hour24 = 0;
            }

            const cronExpression = `${tempMinute} ${hour24} * * *`;
            const updatedSchedules = { ...cronSchedules, [jobKey]: cronExpression };
            const token = localStorage.getItem('token');
            const res = await axios.put(`${API_BASE}/api/business-rules/CRON_SCHEDULES`,
                { value: updatedSchedules },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.status === 'success') {
                setCronSchedules(updatedSchedules);
                setSuccess(`Updated ${jobKey} schedule successfully`);
                setEditing(null);
                setTimeout(() => setSuccess(null), 3000);

                // Set pending changes flag instead of restarting immediately
                setHasPendingChanges(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Update failed');
        }
    };

    const handleToggleActive = async (jobName: string, currentStatus: boolean) => {
        setActionLoading(`toggle-${jobName}`);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`${API_BASE}/api/cron-settings/${jobName}`,
                { isActive: !currentStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.status === 'success') {
                setSettings(prev => prev.map(s => s.jobName === jobName ? { ...s, isActive: !currentStatus } : s));
                setSuccess(`${jobName} is now ${!currentStatus ? 'Active' : 'Deactivated'}`);
                setTimeout(() => setSuccess(null), 3000);

                // Set pending changes flag instead of restarting immediately
                setHasPendingChanges(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Toggle failed');
        } finally {
            setActionLoading(null);
        }
    };

    const handleUpdateRule = async (key: string) => {
        try {
            let finalValue: any;

            // Special handling for dates to maintain ISO format in DB while UI uses datetime-local
            if (key.toLowerCase().includes('date')) {
                const date = new Date(tempRuleValue);
                if (!isNaN(date.getTime())) {
                    finalValue = date.toISOString();
                } else {
                    finalValue = tempRuleValue;
                }
            } else {
                try {
                    finalValue = JSON.parse(tempRuleValue);
                } catch (e) {
                    finalValue = tempRuleValue;
                }
            }

            const token = localStorage.getItem('token');
            const res = await axios.put(`${API_BASE}/api/business-rules/${key}`, { value: finalValue }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.status === 'success') {
                setRules(prev => prev.map(r => r.key === key ? { ...r, value: finalValue } : r));
                setSuccess(`Updated rule ${key} successfully`);
                setEditing(null);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Update failed');
        }
    };

    const handleSystemAction = async (action: 'restart' | 'stop') => {
        if (!window.confirm(`Are you sure you want to ${action} the Cron Service?`)) return;

        if (action === 'restart') {
            await restartCronService();
            return;
        }

        setActionLoading(action);
        try {
            const res = await axios.post(`${CRON_SERVICE_URL}/admin/system/stop`, {}, {
                headers: { 'X-Api-Key': CRON_API_KEY }
            });
            if (res.data.status === 'success') {
                setSuccess(`System stop initiated: ${res.data.data.message}`);
                setServiceHealth('DOWN');
            }
        } catch (err: any) {
            if ((err as any).response?.status === 401) {
                setError(`Authentication failed: Check REACT_APP_CRON_API_KEY in your .env file.`);
            } else {
                setError(`Failed to stop: ` + (err.response?.data?.message || err.message));
            }
        } finally {
            setActionLoading(null);
        }
    };

    const handleManualRun = async (jobName: string) => {
        if (!manualDate) {
            setError('Please select a date for manual run');
            return;
        }

        if (!window.confirm(`Are you sure you want to run ${jobName} for date ${manualDate}?`)) return;

        setRunLoading(jobName);
        try {
            const res = await axios.get(`${CRON_SERVICE_URL}/trigger/${jobName}?apiKey=${CRON_API_KEY}&date=${manualDate}`);
            if (res.data.status === 'success') {
                setSuccess(`Started manual run for ${jobName} on ${manualDate}`);
                setTimeout(() => setSuccess(null), 3000);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Manual run failed');
        } finally {
            setRunLoading(null);
        }
    };


    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                mb: 3,
                gap: 2
            }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>System Manager</Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                bgcolor: serviceHealth === 'UP' ? 'success.50' : serviceHealth === 'IDLE' ? 'info.50' : serviceHealth === 'DEGRADED' ? 'warning.50' : serviceHealth === 'DOWN' ? 'error.50' : serviceHealth === 'RESTARTING' ? 'warning.100' : 'grey.100',
                                border: '1px solid',
                                borderColor: serviceHealth === 'UP' ? 'success.main' : serviceHealth === 'IDLE' ? 'info.main' : serviceHealth === 'DEGRADED' ? 'warning.main' : serviceHealth === 'DOWN' ? 'error.main' : serviceHealth === 'RESTARTING' ? 'warning.dark' : 'grey.400',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: serviceHealth === 'UP' ? 'success.main' : serviceHealth === 'IDLE' ? 'info.main' : serviceHealth === 'DEGRADED' ? 'warning.main' : serviceHealth === 'DOWN' ? 'error.main' : serviceHealth === 'RESTARTING' ? 'warning.main' : 'grey.500',
                                    animation: (serviceHealth === 'CHECKING' || serviceHealth === 'RESTARTING') ? 'pulse 1.5s ease-in-out infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.5 }
                                    }
                                }}
                            />
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                color={serviceHealth === 'UP' ? 'success.dark' : serviceHealth === 'IDLE' ? 'info.dark' : serviceHealth === 'DEGRADED' ? 'warning.dark' : serviceHealth === 'DOWN' ? 'error.dark' : serviceHealth === 'RESTARTING' ? 'warning.dark' : 'text.secondary'}
                            >
                                Cron Service: {serviceHealth === 'UP' ? 'Running' : serviceHealth === 'IDLE' ? 'Idle' : serviceHealth === 'DEGRADED' ? 'Degraded (DB Issue)' : serviceHealth === 'DOWN' ? 'Stopped' : serviceHealth === 'RESTARTING' ? 'Restarting...' : 'Checking...'}
                            </Typography>
                        </Paper>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Configure cron jobs and business rules</Typography>
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 2 },
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    width: { xs: '100%', md: 'auto' }
                }}>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => handleSystemAction('restart')}
                        disabled={serviceHealth === 'DOWN' || !!actionLoading || cooldownSeconds > 0}
                        size="small"
                        sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 120 } }}
                    >
                        {actionLoading === 'restart' ? (
                            <CircularProgress size={20} />
                        ) : cooldownSeconds > 0 ? (
                            `Wait ${cooldownSeconds}s`
                        ) : (
                            'Restart Service'
                        )}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleSystemAction('stop')}
                        disabled={serviceHealth === 'DOWN' || !!actionLoading || cooldownSeconds > 0}
                        size="small"
                        sx={{ flex: { xs: 1, sm: 'none' }, minWidth: { xs: 'auto', sm: 120 } }}
                    >
                        {actionLoading === 'stop' ? <CircularProgress size={20} /> : 'Stop Service'}
                    </Button>
                    <Button
                        startIcon={<RefreshIcon />}
                        onClick={tab === 0 ? fetchSettings : fetchRules}
                        variant="contained"
                        disabled={loading}
                        size="small"
                        sx={{
                            borderRadius: 2,
                            flex: { xs: 1, sm: 'none' },
                            minWidth: { xs: 'auto', sm: 100 }
                        }}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                    mb: 4,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab
                    icon={<ScheduleIcon />}
                    iconPosition="start"
                    label="Cron Schedules"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                />
                <Tab
                    icon={<SettingsIcon />}
                    iconPosition="start"
                    label="Business Rules"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                />
            </Tabs>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {hasPendingChanges && (
                <Alert
                    severity="warning"
                    sx={{ mb: 3, display: 'flex', alignItems: 'center' }}
                    action={
                        <Button
                            color="warning"
                            variant="contained"
                            size="small"
                            onClick={() => restartCronService(false)}
                            disabled={!!actionLoading || cooldownSeconds > 0}
                        >
                            {actionLoading === 'restart' ? <CircularProgress size={20} color="inherit" /> : 'Apply Changes & Restart'}
                        </Button>
                    }
                >
                    {actionLoading === 'restart' ? (
                        <Typography variant="body2" fontWeight={600}>
                            Applying changes and restarting service... Please wait.
                        </Typography>
                    ) : (
                        <Typography variant="body2" fontWeight={600}>
                            You have unapplied cron schedule changes. Click 'Apply Changes & Restart' to make them active.
                        </Typography>
                    )}
                </Alert>
            )}

            {tab === 0 ? (
                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 500, sm: 650 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Job Name</Typography></TableCell>
                                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Description</Typography></TableCell>
                                    <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Schedule</Typography></TableCell>
                                    <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Manual Run</Typography></TableCell>
                                    <TableCell align="right" sx={{ py: { xs: 1.5, sm: 2 } }}><Typography fontWeight={700} variant="body2">Actions</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && Object.keys(cronSchedules).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={30} />
                                        </TableCell>
                                    </TableRow>
                                ) : Object.keys(cronSchedules).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                            <Typography color="text.secondary">No cron jobs configured</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : Object.entries(cronSchedules).map(([jobKey, cronExpr]) => {
                                    const description = settings.find(s => s.jobName === jobKey)?.description || 'System job';
                                    const setting = settings.find(s => s.jobName === jobKey);
                                    const isActive = setting ? setting.isActive : true;
                                    const isActionLoading = actionLoading === `toggle-${jobKey}`;

                                    return (
                                        <TableRow key={jobKey} hover>
                                            <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Switch
                                                        size="small"
                                                        checked={isActive}
                                                        onChange={() => handleToggleActive(jobKey, isActive)}
                                                        disabled={isActionLoading}
                                                    />
                                                    <Typography fontWeight={600} color={isActive ? "primary" : "text.disabled"} variant="body2">{jobKey}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, py: { xs: 1.5, sm: 2 } }}>
                                                <Typography variant="body2" color="text.secondary">{description}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                                {editing === jobKey ? (
                                                    <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, alignItems: 'center', flexWrap: 'wrap', minWidth: { xs: 250, sm: 'auto' } }}>
                                                        <Typography variant="body2" sx={{ minWidth: { xs: 50, sm: 60 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Daily at</Typography>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={tempHour}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (val >= 1 && val <= 12) setTempHour(val);
                                                            }}
                                                            inputProps={{ min: 1, max: 12, style: { width: '50px' } }}
                                                            label="Hour"
                                                        />
                                                        <Typography variant="body2">:</Typography>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={tempMinute}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (val >= 0 && val <= 59) setTempMinute(val);
                                                            }}
                                                            inputProps={{ min: 0, max: 59, style: { width: '50px' } }}
                                                            label="Min"
                                                        />
                                                        <Box sx={{ display: 'flex', gap: 0.5, ml: { xs: 0, sm: 1 } }}>
                                                            <Button
                                                                size="small"
                                                                variant={tempPeriod === 'AM' ? 'contained' : 'outlined'}
                                                                onClick={() => setTempPeriod('AM')}
                                                                sx={{ minWidth: 50, py: 0.5 }}
                                                            >
                                                                AM
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant={tempPeriod === 'PM' ? 'contained' : 'outlined'}
                                                                onClick={() => setTempPeriod('PM')}
                                                                sx={{ minWidth: 50, py: 0.5 }}
                                                            >
                                                                PM
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Paper variant="outlined" sx={{ px: 1.5, py: 0.5, bgcolor: 'primary.50', display: 'inline-block', borderRadius: 2 }}>
                                                            <Typography variant="body2" fontWeight={600} color="primary.dark">
                                                                {cronToReadable(String(cronExpr))}
                                                            </Typography>
                                                        </Paper>
                                                        <Tooltip title={`Cron: ${cronExpr}`}>
                                                            <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', cursor: 'help' }}>
                                                                ⓘ
                                                            </Typography>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ py: { xs: 1.5, sm: 2 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <TextField
                                                        type="date"
                                                        size="small"
                                                        value={manualDate}
                                                        onChange={(e) => setManualDate(e.target.value)}
                                                        sx={{ width: 140 }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => handleManualRun(jobKey)}
                                                        disabled={runLoading === jobKey || !manualDate}
                                                    >
                                                        {runLoading === jobKey ? <CircularProgress size={20} /> : 'Run'}
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                {editing === jobKey ? (
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <Tooltip title="Save Changes">
                                                            <IconButton color="success" size="small" onClick={() => handleUpdateCronSchedule(jobKey)}>
                                                                <SaveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel">
                                                            <IconButton color="error" size="small" onClick={() => setEditing(null)}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<EditIcon />}
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setEditing(jobKey);
                                                                const time = parseCronTime(String(cronExpr));
                                                                // Convert 24-hour to 12-hour
                                                                let hour12 = time.hour;
                                                                let period: 'AM' | 'PM' = 'AM';

                                                                if (time.hour >= 12) {
                                                                    period = 'PM';
                                                                    if (time.hour > 12) hour12 = time.hour - 12;
                                                                } else if (time.hour === 0) {
                                                                    hour12 = 12;
                                                                }

                                                                setTempHour(hour12);
                                                                setTempMinute(time.minute);
                                                                setTempPeriod(period);
                                                            }}
                                                            sx={{ borderRadius: 2 }}
                                                        >
                                                            Edit
                                                        </Button>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            ) : (
                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ minWidth: 180 }}><Typography fontWeight={700}>Rule Key</Typography></TableCell>
                                    <TableCell sx={{ minWidth: 200 }}><Typography fontWeight={700}>Description</Typography></TableCell>
                                    <TableCell><Typography fontWeight={700}>Value</Typography></TableCell>
                                    <TableCell align="right" sx={{ minWidth: 120 }}><Typography fontWeight={700}>Actions</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && rules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={30} />
                                        </TableCell>
                                    </TableRow>
                                ) : rules.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                            <Typography color="text.secondary">No business rules found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rules.map((rule) => {
                                    const isDate = rule.key.toLowerCase().includes('date');
                                    const isObject = typeof rule.value === 'object' && rule.value !== null;

                                    return (
                                        <TableRow key={rule.key} hover>
                                            <TableCell>
                                                <Typography fontWeight={600} color="primary" variant="body2">{rule.key}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{rule.description}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {editing === rule.key && !isObject ? (
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        type={isDate ? 'datetime-local' : 'text'}
                                                        value={tempRuleValue}
                                                        onChange={(e) => setTempRuleValue(e.target.value)}
                                                        sx={{ minWidth: 150 }}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <Box sx={{ py: 1 }}>
                                                        {isDate ? (
                                                            <Typography variant="body2" fontWeight={500}>
                                                                {new Date(rule.value).toLocaleString('en-GB', {
                                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                                    hour: '2-digit', minute: '2-digit', hour12: true
                                                                })}
                                                            </Typography>
                                                        ) : isObject ? (
                                                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                                                                <Grid container spacing={1}>
                                                                    {Object.entries(rule.value || {}).map(([k, v]) => (
                                                                        <Grid item xs={12} key={k}>
                                                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                                                <Typography variant="caption" fontWeight={700} color="primary" sx={{ minWidth: 40, bgcolor: 'primary.50', px: 0.5, borderRadius: 0.5, mt: 0.3 }}>{k}</Typography>
                                                                                <Typography variant="caption" fontWeight={600} color="text.primary">
                                                                                    {Array.isArray(v) ?
                                                                                        v.map((item: any, idx: number) =>
                                                                                            typeof item === 'object' && item !== null
                                                                                                ? `[${idx + 1}] ${Object.entries(item).map(([sk, sv]) => `${sk}: ${sv}`).join(', ')}`
                                                                                                : `[${idx + 1}] ${String(item)}`
                                                                                        ).join(' | ') :
                                                                                        typeof v === 'object' && v !== null ?
                                                                                            Object.entries(v).map(([sk, sv]) => `${sk}: ${sv}`).join(' | ') :
                                                                                            String(v)
                                                                                    }
                                                                                </Typography>
                                                                            </Box>
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                            </Paper>
                                                        ) : (
                                                            <Typography variant="body2" fontWeight={600}>{String(rule.value)}</Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {editing === rule.key && !isObject ? (
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <Tooltip title="Save Changes">
                                                            <IconButton color="success" size="small" onClick={() => handleUpdateRule(rule.key)}>
                                                                <SaveIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel">
                                                            <IconButton color="error" size="small" onClick={() => setEditing(null)}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        startIcon={<EditIcon />}
                                                        variant="outlined"
                                                        onClick={() => {
                                                            setEditing(rule.key);
                                                            if (isObject) {
                                                                setComplexEditRule(rule);
                                                                setTempObjectValue({ ...rule.value });
                                                            } else {
                                                                let val = String(rule.value);
                                                                if (isDate) {
                                                                    try {
                                                                        const d = new Date(rule.value);
                                                                        const year = d.getFullYear();
                                                                        const month = String(d.getMonth() + 1).padStart(2, '0');
                                                                        const day = String(d.getDate()).padStart(2, '0');
                                                                        const hours = String(d.getHours()).padStart(2, '0');
                                                                        const minutes = String(d.getMinutes()).padStart(2, '0');
                                                                        val = `${year}-${month}-${day}T${hours}:${minutes}`;
                                                                    } catch (e) { }
                                                                }
                                                                setTempRuleValue(val);
                                                            }
                                                        }}
                                                        sx={{ borderRadius: 2 }}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Complex Rule Editor Modal */}
            <Dialog
                open={!!complexEditRule}
                onClose={() => setComplexEditRule(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>Edit Rule: {complexEditRule?.key}</Typography>
                    <Typography variant="body2" color="text.secondary">{complexEditRule?.description}</Typography>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ py: 3 }}>
                    <Grid container spacing={2.5}>
                        {complexEditRule && Object.keys(tempObjectValue).map((key) => (
                            <Grid item xs={12} key={key} sx={{ borderBottom: '1px solid', borderColor: 'grey.100', pb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom fontWeight={700} color="primary">{key.toUpperCase()}</Typography>
                                {Array.isArray(tempObjectValue[key]) ? (
                                    // Render array of objects (e.g. tiers in INVESTMENT_WITHDRAWAL_RULES)
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                                        {(tempObjectValue[key] as any[]).map((item: any, idx: number) => (
                                            <Box key={idx} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Tier {idx + 1}</Typography>
                                                <Grid container spacing={2}>
                                                    {typeof item === 'object' && item !== null ? (
                                                        Object.keys(item).map((subKey) => (
                                                            <Grid item xs={6} key={subKey}>
                                                                <TextField
                                                                    label={subKey}
                                                                    fullWidth
                                                                    size="small"
                                                                    value={item[subKey]}
                                                                    onChange={(e) => {
                                                                        const val = e.target.value;
                                                                        const newArr = [...tempObjectValue[key]];
                                                                        newArr[idx] = { ...newArr[idx], [subKey]: isNaN(Number(val)) || val === '' ? val : Number(val) };
                                                                        setTempObjectValue((prev: any) => ({ ...prev, [key]: newArr }));
                                                                    }}
                                                                />
                                                            </Grid>
                                                        ))
                                                    ) : (
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={item}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    const newArr = [...tempObjectValue[key]];
                                                                    newArr[idx] = isNaN(Number(val)) || val === '' ? val : Number(val);
                                                                    setTempObjectValue((prev: any) => ({ ...prev, [key]: newArr }));
                                                                }}
                                                            />
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : typeof tempObjectValue[key] === 'object' && tempObjectValue[key] !== null ? (
                                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        {Object.keys(tempObjectValue[key]).map((subKey) => (
                                            <Grid item xs={6} key={subKey}>
                                                <TextField
                                                    label={subKey}
                                                    fullWidth
                                                    size="small"
                                                    value={tempObjectValue[key][subKey]}
                                                    onChange={(e) => {
                                                        const newVal = { ...tempObjectValue };
                                                        newVal[key] = { ...newVal[key], [subKey]: isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value) };
                                                        setTempObjectValue(newVal);
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                ) : (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={tempObjectValue[key]}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setTempObjectValue((prev: any) => ({
                                                ...prev,
                                                [key]: isNaN(Number(val)) || val === '' ? val : Number(val)
                                            }));
                                        }}
                                    />
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setComplexEditRule(null)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={async () => {
                            if (!complexEditRule) return;
                            try {
                                const token = localStorage.getItem('token');
                                const res = await axios.put(`${API_BASE}/api/business-rules/${complexEditRule.key}`, { value: tempObjectValue }, {
                                    headers: { Authorization: `Bearer ${token}` }
                                });
                                if (res.data.status === 'success') {
                                    setRules(prev => prev.map(r => r.key === complexEditRule.key ? { ...r, value: tempObjectValue } : r));
                                    setSuccess(`Updated rule ${complexEditRule.key} successfully`);
                                    setComplexEditRule(null);
                                    setEditing(null);
                                    setTimeout(() => setSuccess(null), 3000);
                                }
                            } catch (err: any) {
                                setError('Update failed');
                            }
                        }}
                    >
                        Save Rules
                    </Button>
                </DialogActions>
            </Dialog>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'info.50', borderRadius: 3, border: '1px solid', borderColor: 'info.100' }}>
                <Typography variant="subtitle2" color="info.dark" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    💡 Tip:
                </Typography>
                <Typography variant="body2" color="info.dark">
                    {tab === 0 ?
                        "Use format: minute hour day-of-month month day-of-week. Restart service after complex changes." :
                        "Business Rules take immediate effect in calculations. Be careful when editing rule values."
                    }
                </Typography>
            </Box>
        </Box>
    );
};

export default CronManager;
